import React, { useState, useEffect } from 'react';
import { LoanApplication, applicationApi, ApplicationListResponse } from '../services/api';
import LoanApplicationForm from './LoanApplicationForm';
import AnalysisReportForm from './AnalysisReportForm';
import './ApplicationList.css';

const ApplicationList: React.FC = () => {
  const [applications, setApplications] = useState<LoanApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Helper function to get full name
  const getApplicantName = (app: LoanApplication): string => {
    // If new format has first/last name, use that
    if (app.first_name || app.last_name) {
      const parts = [];
      if (app.first_name) parts.push(app.first_name);
      if (app.middle_name) parts.push(app.middle_name);
      if (app.last_name) parts.push(app.last_name);
      return parts.join(' ') || 'No Name';
    }
    // Fall back to legacy applicant_name field
    return app.applicant_name || 'No Name';
  };

  useEffect(() => {
    loadApplications();
    
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, [currentPage, statusFilter]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response: ApplicationListResponse = await applicationApi.getApplications(
        skip, 
        itemsPerPage, 
        statusFilter || undefined
      );
      setApplications(response.applications);
      setTotal(response.total);
    } catch (err) {
      setError('Failed to load applications');
      console.error('Error loading applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewApplication = () => {
    setShowNewForm(true);
    setSelectedApplication(null);
  };

  const handleEditApplication = (applicationId: string) => {
    setSelectedApplication(applicationId);
    setShowNewForm(false);
  };

  const handleViewApplication = (applicationId: string) => {
    setSelectedApplication(applicationId);
    setShowNewForm(false);
  };

  const handleDeleteApplication = async (application: LoanApplication) => {
    const applicantName = getApplicantName(application);
    if (window.confirm(`Are you sure you want to delete the application for "${applicantName}"? This will also delete all associated files.`)) {
      try {
        await applicationApi.deleteApplication(application.application_id);
        await loadApplications();
        
        // Clear selected application if it was deleted
        if (selectedApplication === application.application_id) {
          setSelectedApplication(null);
        }
      } catch (err) {
        setError('Failed to delete application');
        console.error('Error deleting application:', err);
      }
    }
  };

  const handleUpdateStatus = async (application: LoanApplication, newStatus: string) => {
    try {
      await applicationApi.updateApplication(application.application_id, { status: newStatus });
      await loadApplications();
    } catch (err) {
      setError('Failed to update status');
      console.error('Error updating status:', err);
    }
  };

  const handleFormSave = async (savedApplication: LoanApplication) => {
    setShowNewForm(false);
    setSelectedApplication(null);
    
    // Update only the specific application in the list
    setApplications(prevApps => 
      prevApps.map(app => 
        app.application_id === savedApplication.application_id ? savedApplication : app
      )
    );
  };

  const handleFormCancel = async () => {
    setShowNewForm(false);
    const appId = selectedApplication;
    setSelectedApplication(null);
    
    // If we had an application selected (edited/submitted), refresh just that one
    if (appId) {
      try {
        const updatedApp = await applicationApi.getApplication(appId);
        setApplications(prevApps => 
          prevApps.map(app => 
            app.application_id === appId ? updatedApp : app
          )
        );
      } catch (err) {
        console.error('Error refreshing application:', err);
        // Fall back to full reload if single refresh fails
        await loadApplications();
      }
    }
  };

  const handleViewAnalysis = async (applicationId: string) => {
    try {
      setLoadingAnalysis(true);
      const response = await applicationApi.getAnalysisData(applicationId);
      setAnalysisData(response.analysis_data);
      setShowAnalysis(true);
    } catch (err) {
      setError('Failed to load analysis data');
      console.error('Error loading analysis:', err);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleCloseAnalysis = async () => {
    setShowAnalysis(false);
    setAnalysisData(null);
    // Reload applications to reflect any status changes
    await loadApplications();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'submitted': return 'status-submitted';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'under review': return 'status-under-review';
      case 'under_review': return 'status-under-review';
      case 'draft': return 'status-draft';
      default: return 'status-default';
    }
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  // If showing form, render it instead of the list
  if (showNewForm || selectedApplication) {
    return (
      <LoanApplicationForm
        applicationId={selectedApplication || undefined}
        onSave={handleFormSave}
        onCancel={handleFormCancel}
      />
    );
  }

  if (loading) {
    return <div className="loading">Loading applications...</div>;
  }

  return (
    <div className="application-list">
      <div className="list-header">
        <h2>Loan Applications</h2>
        {!currentUser?.is_admin && (
          <div className="header-actions">
            <button
              onClick={handleNewApplication}
              className="btn btn-primary"
            >
              New Application
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="list-controls">
        <div className="filters">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="status-filter"
          >
            <option value="">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="Under Review">Under Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        <div className="list-info">
          <span>
            Showing {applications.length} of {total} applications
          </span>
        </div>
      </div>

      {applications.length === 0 ? (
        <div className="empty-state">
          <h3>No applications found</h3>
          {!currentUser?.is_admin ? (
            <>
              <p>Get started by creating your first loan application.</p>
              <button
                onClick={handleNewApplication}
                className="btn btn-primary"
              >
                Create New Application
              </button>
            </>
          ) : (
            <p>No applications to review at this time.</p>
          )}
        </div>
      ) : (
        <>
          <div className="applications-table-container">
            <table className="applications-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Loan Amount</th>
                  <th>Purpose</th>
                  <th>Status</th>
                  <th>Files</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app.application_id}>
                    <td className="applicant-info">
                      <div className="applicant-name">{getApplicantName(app)}</div>
                      {app.applicant_email && (
                        <div className="applicant-email">{app.applicant_email}</div>
                      )}
                    </td>
                    <td className="loan-amount">
                      {app.loan_amount ? formatCurrency(app.loan_amount) : 'Not specified'}
                    </td>
                    <td className="loan-purpose">
                      {app.loan_purpose || 'Not specified'}
                    </td>
                    <td className="status-cell">
                      {currentUser?.is_admin ? (
                        <select
                          value={app.status}
                          onChange={(e) => handleUpdateStatus(app, e.target.value)}
                          className={`status-select ${getStatusColor(app.status)}`}
                        >
                          <option value="Draft">Draft</option>
                          <option value="Submitted">Submitted</option>
                          <option value="Under Review">Under Review</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      ) : (
                        <span className={`status-badge ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      )}
                    </td>
                    <td className="files-count">
                      <span className="file-badge">{app.file_count} files</span>
                    </td>
                    <td className="created-date">
                      {formatDate(app.created_at)}
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => handleViewApplication(app.application_id)}
                        className="btn btn-sm btn-primary"
                        title="View/Edit Application"
                      >
                        ⓘ
                      </button>
                      {currentUser?.is_admin && ['Under Review', 'Approved', 'Rejected'].includes(app.status) && (
                        <button
                          onClick={() => handleViewAnalysis(app.application_id)}
                          className="btn btn-sm btn-analysis"
                          title="View Analysis Report"
                          disabled={loadingAnalysis}
                        >
                          {loadingAnalysis ? '⏳' : '📊'}
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteApplication(app)}
                        className="btn btn-sm btn-danger"
                        title="Delete Application"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="btn btn-sm btn-secondary"
              >
                Previous
              </button>
              
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="btn btn-sm btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Analysis Report Modal */}
      {showAnalysis && analysisData && (
        <AnalysisReportForm
          data={analysisData}
          onClose={handleCloseAnalysis}
        />
      )}
    </div>
  );
};

export default ApplicationList;